import React, { FC, MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import QRCode from 'qrcode';

// Large enough to stay sharp on printed material; the on-screen preview scales the same image down.
const QR_PIXEL_SIZE = 1024;
const QR_PREVIEW_SIZE = '7.5rem';
// The downloaded code carries the 4-module quiet zone ISO/IEC 18004 asks for, so it still scans once
// it is printed onto a coloured or busy background. The preview keeps the tighter margin purely for
// how it sits inside the popover, where nothing is printed around it.
const QR_PREVIEW_MARGIN = 2;
const QR_DOWNLOAD_MARGIN = 4;

interface Props {
  url: string;
  slug: string;
}

const SurveyLinkPopover: FC<Props> = ({ url, slug }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [previewDataUrl, setPreviewDataUrl] = useState<string>('');
  const [downloadDataUrl, setDownloadDataUrl] = useState<string>('');
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const isOpen = Boolean(anchorEl);

  // Encoded on first open rather than on mount, so a domain with many surveys does not build
  // a code for every row of a list nobody has interacted with yet.
  useEffect(() => {
    if (!isOpen || previewDataUrl) return;

    Promise.all([
      QRCode.toDataURL(url, { width: QR_PIXEL_SIZE, margin: QR_PREVIEW_MARGIN }),
      QRCode.toDataURL(url, { width: QR_PIXEL_SIZE, margin: QR_DOWNLOAD_MARGIN }),
    ])
      .then(([preview, download]) => {
        setPreviewDataUrl(preview);
        setDownloadDataUrl(download);
      })
      .catch((error) => console.error('Failed to generate survey QR code: ', error));
  }, [isOpen, previewDataUrl, url]);

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy survey link: ', error);
    }
  };

  const handleDownloadClick = () => {
    if (!downloadDataUrl) return;

    const link = document.createElement('a');
    link.href = downloadDataUrl;
    link.download = `qr-${slug}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Tooltip
        title={t('survey_link_qr')}
        arrow
      >
        <IconButton
          size="small"
          onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
          sx={{ ml: 'auto' }}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ '& .MuiPaper-root': { p: theme.spacing(2) } }}
      >
        <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1.5),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <LinkIcon color="primary" />
              <Typography
                variant="body2"
                sx={{ fontWeight: 700 }}
              >
                {t('copy_link')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <TextField
                size="small"
                value={url}
                slotProps={{ htmlInput: { readOnly: true, 'aria-label': t('copy_link') } }}
                sx={{ minWidth: '17.75rem' }}
              />
              <Button
                variant="contained"
                startIcon={isCopied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
                onClick={handleCopyClick}
                sx={{ flexShrink: 0 }}
              >
                {t('copy')}
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<QrCode2Icon />}
              onClick={handleDownloadClick}
              disabled={!downloadDataUrl}
            >
              {t('download_qr_code')}
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: QR_PREVIEW_SIZE,
              height: QR_PREVIEW_SIZE,
              p: theme.spacing(1),
              backgroundColor: theme.palette.common.white,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: `${theme.shape.borderRadius}px`,
            }}
          >
            {previewDataUrl && (
              <img
                src={previewDataUrl}
                alt={t('survey_link_qr')}
                style={{ width: '100%', height: '100%' }}
              />
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default SurveyLinkPopover;
